// Certification tab, what the certifier proved about each spend path. A flat
// summary strip up top mirrors the funding gate; below it, every leaf gets a
// small tone dot + assurance label (proven / unproven / divergence) and a human
// rendering of its CertStatus: the proof method, the domain it covers, or the
// precise reason it could not be proven. This is the tab refusals point to.

import type { ReactNode } from "react";
import { ListChecks } from "lucide-react";
import { useIde, selectActiveContract } from "../store";
import type { Assurance, CertReport, CertStatus, Gate } from "../types";
import { EmptyState, SectionHead, parseExterns, runHint } from "./shared";

const ASSURANCE_LABEL: Record<Assurance, string> = {
  proven: "Proven",
  unproven: "Unproven",
  divergence: "Divergence",
};

/** A short, human description of HOW the leaf reached its assurance, plus the
 *  one-line method tag shown beneath it. */
function describeStatus(status: CertStatus): { detail: ReactNode; method: string } {
  switch (status.kind) {
    case "certified":
      return {
        detail: (
          <>
            Exhaustively certified, every one of <b>{status.checked.toLocaleString()}</b>{" "}
            {status.checked === 1 ? "witness" : "witnesses"} was checked.
          </>
        ),
        method: "exhaustive enumeration",
      };
    case "proven_int":
      return {
        detail: (
          <>
            Proven over the full integer domain of{" "}
            <code className="pn-mono-inline">{status.var}</code> via affine cell decomposition (
            <b>{status.cells.toLocaleString()}</b> {status.cells === 1 ? "cell" : "cells"}).
          </>
        ),
        method: "symbolic Int decision procedure",
      };
    case "proven_symbolic":
      return {
        detail: (
          <>
            Proven symbolically, the spend condition holds for all assignments of its{" "}
            <b>{status.atoms.toLocaleString()}</b> symbolic{" "}
            {status.atoms === 1 ? "atom" : "atoms"}.
          </>
        ),
        method: "full symbolic equivalence",
      };
    case "t2_only_symbolic":
      return {
        detail: (
          <>
            Proven by structural (T2) equivalence over <b>{status.atoms.toLocaleString()}</b>{" "}
            {status.atoms === 1 ? "atom" : "atoms"}. The breakpoint engine (T1) did not apply:{" "}
            {status.t1Reason}
          </>
        ),
        method: "T2 structural equivalence",
      };
    case "differential":
      return {
        detail: (
          <>
            Differentially certified against <b>{status.checked.toLocaleString()}</b> reference{" "}
            {status.checked === 1 ? "execution" : "executions"}: {status.reason}
          </>
        ),
        method: "differential execution",
      };
    case "bounded_checked":
      return {
        detail: (
          <>
            Checked over the bounded range{" "}
            <code className="pn-mono-inline">
              [{status.lo}, {status.hi}]
            </code>:{" "}
            <b>{status.checked.toLocaleString()}</b>{" "}
            {status.checked === 1 ? "point" : "points"} enumerated.
          </>
        ),
        method: "bounded enumeration",
      };
    case "unbounded":
      return {
        detail: <>Not proven, {status.reason}</>,
        method: "domain is unbounded",
      };
    case "divergence":
      return {
        detail: (
          <>
            <b className="pn-cert-diverge-tag">Divergence.</b> {status.detail}
          </>
        ),
        method: "certified script contradicts the source",
      };
  }
}

function CertRow({ report }: { report: CertReport }) {
  const { detail, method } = describeStatus(report.status);
  return (
    <div className="pn-cert-row">
      <span className={`pn-cert-badge pn-cert-badge--${report.assurance}`}>
        <span className="pn-cert-dot" aria-hidden="true" />
        {ASSURANCE_LABEL[report.assurance]}
      </span>
      <div className="pn-cert-info">
        <div className="pn-cert-name">{report.name}</div>
        <div className="pn-cert-detail">{detail}</div>
        <div className="pn-cert-method">{method}</div>
      </div>
    </div>
  );
}

type SummaryTone = "proven" | "unproven" | "divergence";

function GateSummary({ gate, reports }: { gate: Gate | undefined; reports: CertReport[] }) {
  const proven = reports.filter((r) => r.assurance === "proven").length;
  const unproven = reports.filter((r) => r.assurance === "unproven").length;
  const divergence = reports.filter((r) => r.assurance === "divergence").length;

  const tone: SummaryTone =
    divergence > 0 ? "divergence" : unproven > 0 || gate?.coverageGap ? "unproven" : "proven";
  const allProven = unproven === 0 && divergence === 0;

  let title: string;
  let sub: ReactNode;
  if (divergence > 0) {
    title = "Divergence, funding blocked";
    sub = "At least one leaf's certified behavior contradicts the source. Do not fund.";
  } else if (gate?.mayProceed && allProven) {
    title = "Fully certified, safe to fund";
    sub = "Every spend path is proven over its full domain.";
  } else if (unproven > 0 || gate?.coverageGap) {
    title = "Partially certified, not fundable";
    sub = gate?.coverageGap
      ? "A coverage gap remains: not every reachable spend path is certified."
      : `${unproven} ${unproven === 1 ? "leaf" : "leaves"} could not be proven over the full domain.`;
  } else {
    title = "Certification";
    sub = "Per-leaf assurance for the compiled contract.";
  }

  return (
    <div className={`pn-cert-summary pn-cert-summary--${tone}`}>
      <div className="pn-cert-summary-main">
        <div className="pn-cert-summary-title">{title}</div>
        <div className="pn-cert-summary-sub">{sub}</div>
      </div>
      <div className="pn-cert-tally">
        <div className="pn-cert-tally-item">
          <span className="pn-cert-tally-num pn-cert-tally-num--proven">{proven}</span>
          <span className="pn-cert-tally-label">proven</span>
        </div>
        <span className="pn-cert-tally-sep" aria-hidden="true" />
        <div className="pn-cert-tally-item">
          <span className="pn-cert-tally-num pn-cert-tally-num--unproven">{unproven}</span>
          <span className="pn-cert-tally-label">unproven</span>
        </div>
        <span className="pn-cert-tally-sep" aria-hidden="true" />
        <div className="pn-cert-tally-item">
          <span className="pn-cert-tally-num pn-cert-tally-num--divergence">{divergence}</span>
          <span className="pn-cert-tally-label">diverge</span>
        </div>
      </div>
    </div>
  );
}

export function CertificationPanel() {
  const result = useIde((s) => s.result);
  const contract = useIde(selectActiveContract);
  const reports = result?.certification;

  if (!reports || reports.length === 0) {
    // A compile that produced no gate had no leaves to certify -- almost always
    // because externs are unbound. Say so, instead of the bare "nothing here".
    const externs = contract ? parseExterns(contract.content) : [];
    const needsValues = !!result && !result.gate && externs.length > 0;
    return (
      <div className="pn-scroll">
        <EmptyState
          icon={<ListChecks size={22} strokeWidth={1.5} />}
          title={needsValues ? "Bind values first" : "No certification yet"}
          hint={
            needsValues ? (
              <>
                Certification runs once {externs.length === 1 ? "the extern is" : "externs are"} bound.
                Set {externs.map((e) => e.name).join(", ")} in the args file, then compile.
              </>
            ) : (
              runHint("per-leaf proof results")
            )
          }
        />
      </div>
    );
  }

  return (
    <div className="pn-scroll">
      <GateSummary gate={result?.gate} reports={reports} />
      <SectionHead
        title="Per-leaf assurance"
        count={`${reports.length} ${reports.length === 1 ? "leaf" : "leaves"}`}
      />
      <div className="pn-cert-list">
        {reports.map((report) => (
          <CertRow key={report.name} report={report} />
        ))}
      </div>
    </div>
  );
}
